import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, UseFormGetValues } from 'react-hook-form';
import styled from 'styled-components';
import Space from 'components/UI/Space';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid';
import Text from 'components/UI/Text';
import { useStores } from 'stores/hooks/hooks';
import { DEFAULT_USER_PROFILE_ID } from 'core/constants';
import { UserPreferences } from 'models/user';
import Button from 'components/UI/Button';
import { theme } from 'components/UI';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { UserFormFieldsSchema } from './UserManagementFormSchemas';

interface UserPreferencesEdit {
  editUserPreferencesValues: {
    profileId: number;
    shouldReset: boolean;
  };
  handleChangeEditUserPreferencesValues: (values: {
    profileId: number;
    shouldReset: boolean;
  }) => void;
}

const NoWrappingText = styled(Text)`
  white-space: nowrap;
`;

const UserPreferencesEdit: FC<UserPreferencesEdit> = observer(
  ({ editUserPreferencesValues, handleChangeEditUserPreferencesValues }) => {
    const { t } = useTranslation('users');
    const { membersStore } = useStores();

    const handleChangeProfileId = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChangeEditUserPreferencesValues({
          profileId: Number(event.target.value),
          shouldReset: editUserPreferencesValues.shouldReset,
        });
      },
      [
        handleChangeEditUserPreferencesValues,
        editUserPreferencesValues.shouldReset,
      ],
    );

    const handleShouldResetChange = useCallback(
      (event: CheckboxChangeEvent) => {
        handleChangeEditUserPreferencesValues({
          profileId: editUserPreferencesValues.profileId,
          shouldReset: !!event.target.checked,
        });
      },
      [
        handleChangeEditUserPreferencesValues,
        editUserPreferencesValues.profileId,
      ],
    );

    return (
      <>
        <Space height={1} />
        <Grid direction="column" gap="1rem" align="stretch">
          <Grid direction="row" gap="2.1rem" grow>
            <NoWrappingText>{t('user_origin')}:</NoWrappingText>
            <Input
              select
              fullWidth
              value={editUserPreferencesValues.profileId}
              label={t('user_origin')}
              defaultValue={DEFAULT_USER_PROFILE_ID}
              onChange={handleChangeProfileId}
              SelectProps={{
                native: true,
              }}
            >
              {JSON.parse(JSON.stringify(membersStore.userPreferences)).map(
                (option: UserPreferences) => (
                  <option key={option.profileId} value={option.profileId}>
                    {t(option.profileDescription)}
                  </option>
                ),
              )}
            </Input>
            <NoWrappingText>{t('should_reset')}:</NoWrappingText>
            <Checkbox
              title="Should Reset"
              checked={editUserPreferencesValues.shouldReset}
              value={editUserPreferencesValues.shouldReset}
              onChange={handleShouldResetChange}
            />
          </Grid>
          <Space height={3} />
        </Grid>
        <Space height={1} />
      </>
    );
  },
);

export default UserPreferencesEdit;
